import React, { FC } from 'react'
import ActionGroup from '@pixel/core/ActionGroup'
import Box from '@pixel/core/Box'
import type { ButtonProps } from '@pixel/core/Button'
import DataTable from '@pixel/core/DataTable'
import Link from '@pixel/core/Link'
import Paper from '@pixel/core/Paper'
import Typography from '@pixel/core/Typography'
import { styled } from '@pixel/core/styles'
import LocalizedTypography from '../../components/LocalizedTypography'
import {AutomationSearchResult, AutomationStatusType} from '../types/Automations'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import {useLocalizedText, useMultipleLocalizedText} from '../../contexts/Localization/localizationHooks'
import { CompanyAutomationsListBaseNodeContext } from './CompanyAutomationsListBase'

const automationStatusTranslationMap = {
    active: LocalizedText.AUTOMATION_STATUS_ACTIVE,
    inactive: LocalizedText.AUTOMATION_STATUS_INACTIVE,
} as {
    [key in AutomationSearchResult['status']]: LocalizedText
}

const automationStatusColorMap = {
    active: 'green' as 'success',
    inactive: 'error',
} as {
    [key in AutomationSearchResult['status']]: ButtonProps['color']
}

const automationTypeTranslationMap = {
    'AUTOMATED': LocalizedText.AUTOMATION_TYPE_AUTO,
    'ON_DEMAND': LocalizedText.AUTOMATION_TYPE_ONDEMAND,
} as {
    [key in AutomationSearchResult['automationType']]: LocalizedText
}

const automationObjectTranslationMap = {
    'REQUISITION': LocalizedText.AUTOMATION_TRIGGER_REQUISITION,
    'CANDIDATE': LocalizedText.AUTOMATION_TRIGGER_CANDIDATE,
} as {
    [key in AutomationSearchResult['automationObject']]: LocalizedText
}

const ALL_POSSIBLE_MATCHES = [
    LocalizedText.AUTOMATION_FILTER_REQ_CATEGORY,
    LocalizedText.AUTOMATION_FILTER_REQ_DEPARTMENT,
    LocalizedText.AUTOMATION_FILTER_REQ_JOB_TYPE,
    LocalizedText.AUTOMATION_FILTER_REQ_JOB_LOCATION,
    LocalizedText.AUTOMATION_FILTER_REQ_STATUS,
    LocalizedText.AUTOMATION_FILTER_APPLICATION_ACTIVITY,
    LocalizedText.AUTOMATION_FILTER_REQ_WORKFLOW,
    LocalizedText.AUTOMATION_FILTER_ACTIVE_APPLICATIONS,
    LocalizedText.AUTOMATION_FILTER_COMPANY_NAME,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_EMAIL_TEMPLATE_ID,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_WORKFLOW_STATE_TITLE,
    LocalizedText.AUTOMATION_ACTION_FIELD_CANDIDATE_DISPOSITION_ID,
    LocalizedText.AUTOMATION_FILTER_CANDIDATE_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_FILTER_CANDIDATE_DAYS_ON_WORKFLOW_STATE,
    LocalizedText.AUTOMATION_TRIGGER_CANDIDATE_WORKFLOW_STATE,
];

const translateDisplayValues = (values: string) => {
    const translationsMap: Record<string, string> = {};

    ALL_POSSIBLE_MATCHES.forEach((match) => {
        const translation = useLocalizedText(match);
        if (translation) {
            translationsMap[match] = translation;
        }
    });

    let translatedString = values;
    Object.keys(translationsMap).forEach((match) => {
        translatedString = translatedString.replace(new RegExp(`{${match}}`, 'g'), translationsMap[match]);
    });

    return translatedString;
};


export const AutomationsDataTableColumns = [
    {
        id: 'name',
        columnName: window.i18n.automationEngine.HEADER_NAME,
        minWidth: 150,
        renderCell: (node: AutomationSearchResult, nodeContext: CompanyAutomationsListBaseNodeContext) => (
            <Link data-id={`automation-${node.companyAutomationId}-name-cell`} role="button" onClick={(event: any) => {
                event.preventDefault()
                nodeContext.viewAutomationSelected(node)
            }}>{node.name}</Link>
        )
    },
    {
        id: 'status',
        columnName: window.i18n.automationEngine.HEADER_STATUS,
        minWidth: 80,
        maxWidth: 90,
        renderCell: (node: AutomationSearchResult) => (
            <LocalizedTypography data-id={`automation-${node.companyAutomationId}-status-cell`}
                color={automationStatusColorMap[node.status.toLowerCase() as AutomationStatusType]}
                text={automationStatusTranslationMap[node.status.toLowerCase() as AutomationStatusType]}
            />
        )
    },
    {
        id: 'type',
        columnName: window.i18n.automationEngine.HEADER_TYPE,
        minWidth: 80,
        maxWidth: 150,
        renderCell: (node: AutomationSearchResult) => (
            <LocalizedTypography data-id={`automation-${node.companyAutomationId}-type-cell`} text={automationTypeTranslationMap[node.automationType]}  />
        )
    },
    {
        id: 'trigger',
        columnName: window.i18n.automationEngine.HEADER_TRIGGER,
        minWidth: 150,
        maxWidth: 200,
        renderCell: (node: AutomationSearchResult) => (
            <LocalizedTypography data-id={`automation-${node.companyAutomationId}-trigger-cell`} text={automationObjectTranslationMap[node.automationObject]}  />
        )
    },
    {
        id: 'condition',
        columnName: window.i18n.automationEngine.HEADER_CONDITION,
        minWidth: 150,
        maxWidth: 200,
        renderCell: (node: AutomationSearchResult) => <Typography data-id={`automation-${node.companyAutomationId}-condition-cell`} className='ellipsis' title={translateDisplayValues(node.displayFilters)}>{translateDisplayValues(node.displayFilters)}</Typography>
    },
    {
        id: 'action',
        columnName: window.i18n.automationEngine.HEADER_ACTION,
        minWidth: 150,
        maxWidth: 300,
        renderCell: (node: AutomationSearchResult) => <Typography data-id={`automation-${node.companyAutomationId}-action-cell`} className='ellipsis' title={translateDisplayValues(node.displayActions)}>{translateDisplayValues(node.displayActions)}</Typography>
    },
    {
        id: 'lastUpdatedDate',
        columnName: window.i18n.automationEngine.HEADER_LASTUPDATED,
        minWidth: 150,
        maxWidth: 180,
        renderCell: (node: AutomationSearchResult) => (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Typography data-id={`automation-${node.companyAutomationId}-last-updated-date-cell`}>{new Date(node.lastUpdatedDate).toLocaleDateString('en-US') }</Typography>
                <Typography data-id={`automation-${node.companyAutomationId}-last-updated-date-user-cell`}>{window.i18n.automationEngine.AUTOMATION_UPDATEDBY.replace(':name', node.lastUpdateUserFullName)}</Typography>
            </div>
        ),
    },
]

export const AutomationsDataTableColumnNames = AutomationsDataTableColumns.map(column => column.columnName)

const AutomationsDataTableContainer = styled('div')(({ }: { theme: any }) => ({

}))

interface AutomationsDataTableProps {
    viewContextProps: any
    dataStatus: 'loading' | 'success' | 'error'
    totalCount: number
    onRefetch: () => void
    onAutomationSelected: (automation: AutomationSearchResult) => void
    viewAutomationSelected: (automation: AutomationSearchResult) => void
    changeAutomationStatus: (automation: AutomationSearchResult) => void
}

export const AutomationsDataTable: FC<AutomationsDataTableProps> = ({
    viewContextProps,
    dataStatus,
    totalCount,
    onAutomationSelected,
    viewAutomationSelected,
    changeAutomationStatus,
}) => {
    const {
        AUTOMATION_STATUS_MAKE,
        AUTOMATION_STATUS_ACTIVE,
        AUTOMATION_STATUS_INACTIVE,
        AUTOMATION_EDIT,
        AUTOMATION_OVERVIEW,
    } = useMultipleLocalizedText()

    if (dataStatus === 'error') {
        return (
            <Paper>
                <Box display="flex" padding={2} alignItems="center" flexDirection="column" justifyContent="center">
                    <LocalizedTypography textAlign="center" color="error" text={LocalizedText.AUTOMATION_LIST_ERROR_FETCH} />
                </Box>
            </Paper>
        )
    }

    if (totalCount === 0) {
        return (
            <Paper>
                <Box padding={2} alignItems="center">
                    <LocalizedTypography textAlign="center" text={LocalizedText.AUTOMATION_LIST_NO_RESULTS} />
                </Box>
            </Paper>
        )
    }

    return (
        <AutomationsDataTableContainer>
            <DataTable
                {...viewContextProps}
                columns={AutomationsDataTableColumns}
                renderActions={(node: AutomationSearchResult) => (
                    <ActionGroup
                        id={`action-group=${node.companyAutomationId}`}
                        actions={[
                            {
                                label: AUTOMATION_EDIT,
                                onClick: () => {
                                    onAutomationSelected(node)
                                }
                            },
                            {
                                label: AUTOMATION_STATUS_MAKE.replace(':status',
                                    node.status !== 'ACTIVE' ?
                                        AUTOMATION_STATUS_ACTIVE :
                                        AUTOMATION_STATUS_INACTIVE
                                ),
                                onClick: () => {
                                    changeAutomationStatus(node)
                                }
                            },
                            {
                                label: AUTOMATION_OVERVIEW,
                                onClick: () => {
                                    viewAutomationSelected(node)
                                }
                            },
                        ]}
                    />
                )}
            />
        </AutomationsDataTableContainer>
    )
}

export default AutomationsDataTable
